'use client';

import { c as _c } from "react/compiler-runtime";
import { textInputClassName } from '@/components/ui/field';
import type { MaterialOrderForm, MaterialOrderItem, MaterialOrderOfHof, MaterialOrderVersion } from '@/features/hof-dashboard/api/material-orders';
import { LABEL_CLASS, Panel, PRIMARY_BUTTON_CLASS, QUIET_BUTTON_CLASS, SECONDARY_BUTTON_CLASS, SectionHeading, StatusPill } from '@/features/hof-dashboard/components/dashboard-ui';
import { Timeline } from '@/features/hof-dashboard/components/review-timeline';
import { HOF_ENTRY_STATUS_LABELS, type HofReviewChoice } from '@/features/hof-dashboard/constants';
import { formatDate, formatNumber, translate, type TextKey } from '@/features/hof-dashboard/texts';
import { notifyFailure } from '@/features/hof-dashboard/utils/notify-failure';
import { reviewSteps, type TimelineStep } from '@/features/hof-dashboard/utils/review-steps';
import { trpc } from '@/trpc/client';
import type { Locale } from '@/types/types';
import { cn } from '@/utils/tailwindcss-override';
import { ChevronLeft, Lock, RotateCcw, Undo2 } from 'lucide-react';
import type React from 'react';
import { useId, useState } from 'react';
import { toast } from 'sonner';

/** One line of the order: what the Hof ordered now, and before. */
interface OrderLine {
  item: MaterialOrderItem;
  quantity: number;
  previous: number | undefined;
}

/** The lines of the newest version and those it dropped, in the form's order. */
const orderLines = (form: MaterialOrderForm, current: Record<string, number>, previous: Record<string, number> | undefined): OrderLine[] => form.items.flatMap((item): OrderLine[] => {
  const quantity = current[item.id] ?? 0;
  const before = previous === undefined ? undefined : previous[item.id] ?? 0;
  return quantity === 0 && (before ?? 0) === 0 ? [] : [{
    item,
    quantity,
    previous: before
  }];
});

/** What changed from one version to the next, one line per change. */
const changesOf = (form: MaterialOrderForm, version: MaterialOrderVersion, previous: MaterialOrderVersion | undefined, locale: Locale): string[] => {
  if (previous === undefined) {
    return [translate('positionsOrdered', locale, {
      n: Object.keys(version.quantities).length
    })];
  }
  const changes = orderLines(form, version.quantities, previous.quantities).filter(line => line.quantity !== line.previous).map(line => `${line.item.name}: ${formatNumber(line.previous ?? 0, locale)} → ${formatNumber(line.quantity, locale)}`);
  const remarks = (entry: MaterialOrderVersion): string => JSON.stringify(entry.remarks.map(remark => remark.text));
  if (remarks(version) !== remarks(previous)) changes.push(translate('remarksChanged', locale));
  return changes;
};

/** The order's history, newest first: every version handed in, and every answer of the Ressort. */
const historyOf = (form: MaterialOrderForm, versions: readonly MaterialOrderVersion[], locale: Locale): TimelineStep[] => {
  const oldestFirst = versions.toReversed();
  const steps = oldestFirst.flatMap((version, index): TimelineStep[] => [{
    at: version.submittedAt,
    title: translate('versionHandedIn', locale, {
      n: index + 1
    }),
    by: translate('hof', locale),
    lines: changesOf(form, version, oldestFirst[index - 1], locale),
    quote: undefined,
    tone: 'version'
  }, ...reviewSteps(version.reviewLog, locale)]);
  return steps.toSorted((a, b) => b.at.localeCompare(a.at));
};

/** The change of a line as a signed number, or that it is new. */
const changeLabel = (line: OrderLine, locale: Locale): string | undefined => {
  if (line.previous === undefined || line.previous === line.quantity) return undefined;
  if (line.previous === 0) return translate('newLine', locale);
  const difference = line.quantity - line.previous;
  return `${difference > 0 ? '+' : '−'}${formatNumber(Math.abs(difference), locale)}`;
};

/** What the answer bar says: what the Ressort answered last, or what it can do. */
const answerHint = (version: MaterialOrderVersion, confirmed: boolean): TextKey => {
  if (confirmed) return 'orderConfirmed';
  return version.status === 'revisionRequired' ? 'orderRejected' : 'reviewOrderHint';
};

/** The Ressort's answer on the newest version: send it back with a reason, or confirm it. */
const ReviewActions: React.FC<{
  hofId: string;
  version: MaterialOrderVersion;
  locale: Locale;
}> = t0 => {
  const $ = _c(35);
  const {
    hofId,
    version,
    locale
  } = t0;
  const utils = trpc.useUtils();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      networkMode: "always"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const mutation = trpc.hofDashboard.updateSubmissionReview.useMutation(t1);
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState(version.feedback);
  const reasonId = useId();
  const confirmed = version.status === "accepted" && version.final;
  let t2;
  if ($[1] !== hofId || $[2] !== locale || $[3] !== mutation || $[4] !== utils.hofDashboard.getHofDashboard || $[5] !== utils.hofDashboard.getHofOverview || $[6] !== utils.hofDashboard.getMaterialOrderList || $[7] !== version.id) {
    t2 = async (status, comment, final) => {
      try {
        await mutation.mutateAsync({
          hofId,
          submissionId: version.id,
          status,
          comment,
          final
        });
        toast.success(translate("saved", locale));
        setRejecting(false);
      } catch {
        notifyFailure(locale, "reviewFailed");
      }
      await Promise.all([utils.hofDashboard.getMaterialOrderList.invalidate(), utils.hofDashboard.getHofOverview.invalidate(), utils.hofDashboard.getHofDashboard.invalidate({
        hofId
      })]);
    };
    $[1] = hofId;
    $[2] = locale;
    $[3] = mutation;
    $[4] = utils.hofDashboard.getHofDashboard;
    $[5] = utils.hofDashboard.getHofOverview;
    $[6] = utils.hofDashboard.getMaterialOrderList;
    $[7] = version.id;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  const answer = t2;
  let t3;
  if ($[9] !== confirmed || $[10] !== locale || $[11] !== version) {
    t3 = translate(answerHint(version, confirmed), locale);
    $[9] = confirmed;
    $[10] = locale;
    $[11] = version;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  let t4;
  if ($[13] !== t3) {
    t4 = <p className="max-w-xl text-base text-pretty text-green-800">{t3}</p>;
    $[13] = t3;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  let t5;
  if ($[15] !== answer || $[16] !== confirmed || $[17] !== locale || $[18] !== mutation.isPending || $[19] !== rejecting) {
    t5 = <div className="flex flex-wrap gap-2">{confirmed ? <button type="button" className={SECONDARY_BUTTON_CLASS} disabled={mutation.isPending} onClick={() => void answer(undefined, undefined, false)}><RotateCcw aria-hidden={true} />{translate("reopen", locale)}</button> : <><button type="button" aria-expanded={rejecting} className={cn(SECONDARY_BUTTON_CLASS, "border-amber-300 text-amber-800 hover:bg-amber-50")} disabled={mutation.isPending} onClick={() => setRejecting(_temp)}><Undo2 aria-hidden={true} />{translate("reject", locale)}</button><button type="button" className={PRIMARY_BUTTON_CLASS} disabled={mutation.isPending} onClick={() => void answer("accepted", undefined, true)}><Lock aria-hidden={true} />{translate("confirmOrder", locale)}</button></>}</div>;
    $[15] = answer;
    $[16] = confirmed;
    $[17] = locale;
    $[18] = mutation.isPending;
    $[19] = rejecting;
    $[20] = t5;
  } else {
    t5 = $[20];
  }
  let t6;
  if ($[21] !== t4 || $[22] !== t5) {
    t6 = <div className="flex flex-wrap items-center justify-between gap-4">{t4}{t5}</div>;
    $[21] = t4;
    $[22] = t5;
    $[23] = t6;
  } else {
    t6 = $[23];
  }
  let t7;
  if ($[24] !== answer || $[25] !== confirmed || $[26] !== locale || $[27] !== mutation.isPending || $[28] !== reason || $[29] !== reasonId || $[30] !== rejecting) {
    t7 = rejecting && !confirmed && <form className="space-y-3" onSubmit={event => {
      event.preventDefault();
      answer("revisionRequired", reason.trim(), false);
    }}><label htmlFor={reasonId} className="block text-sm font-semibold text-green-900">{translate("rejectReason", locale)}</label><textarea id={reasonId} rows={3} required={true} maxLength={5000} value={reason} onChange={event_0 => setReason(event_0.target.value)} className={textInputClassName} /><div className="flex flex-wrap gap-2"><button type="submit" className={PRIMARY_BUTTON_CLASS} disabled={mutation.isPending}>{translate("reject", locale)}</button><button type="button" className={SECONDARY_BUTTON_CLASS} onClick={() => setRejecting(false)}>{translate("cancel", locale)}</button></div></form>;
    $[24] = answer;
    $[25] = confirmed;
    $[26] = locale;
    $[27] = mutation.isPending;
    $[28] = reason;
    $[29] = reasonId;
    $[30] = rejecting;
    $[31] = t7;
  } else {
    t7 = $[31];
  }
  let t8;
  if ($[32] !== t6 || $[33] !== t7) {
    t8 = <Panel className="space-y-4">{t6}{t7}</Panel>;
    $[32] = t6;
    $[33] = t7;
    $[34] = t8;
  } else {
    t8 = $[34];
  }
  return t8;
};

/**
 * One Hof's material order, for the Ressort: what it ordered now and how that changed from the
 * version before, its remarks, the history of versions and answers, and the answer itself.
 * Every other answer, such as feedback alone, is given on the Hof's dashboard.
 */
export const MaterialOrderReview: React.FC<{
  form: MaterialOrderForm;
  hof: MaterialOrderOfHof;
  onBack: () => void;
  onOpenInDashboard: () => void;
  locale: Locale;
}> = t0 => {
  const $ = _c(36);
  const {
    form,
    hof,
    onBack,
    onOpenInDashboard,
    locale
  } = t0;
  const [current, previous] = hof.versions;
  const t1 = form.mode === "versions" ? previous?.quantities : undefined;
  let sections;
  if ($[0] !== form || $[1] !== hof.quantities || $[2] !== t1) {
    const lines = orderLines(form, hof.quantities, t1);
    sections = [];
    for (const line of lines) {
      const last = sections.at(-1);
      if (last !== undefined && last.section === line.item.section) {
        last.lines.push(line);
      } else {
        sections.push({
          section: line.item.section,
          lines: [line]
        });
      }
    }
    $[0] = form;
    $[1] = hof.quantities;
    $[2] = t1;
    $[3] = sections;
  } else {
    sections = $[3];
  }
  let t2;
  if ($[4] !== current || $[5] !== form || $[6] !== hof.hofId || $[7] !== hof.hofName || $[8] !== hof.versions || $[9] !== locale || $[10] !== onBack || $[11] !== onOpenInDashboard || $[12] !== sections) {
    const history = historyOf(form, hof.versions, locale);
    let t3;
    if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = cn(QUIET_BUTTON_CLASS, "-ml-3");
      $[14] = t3;
    } else {
      t3 = $[14];
    }
    let t4;
    if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = <ChevronLeft aria-hidden={true} />;
      $[15] = t4;
    } else {
      t4 = $[15];
    }
    let t5;
    if ($[16] !== locale) {
      t5 = translate("allMaterialOrders", locale);
      $[16] = locale;
      $[17] = t5;
    } else {
      t5 = $[17];
    }
    let t6;
    if ($[18] !== onBack || $[19] !== t5) {
      t6 = <button type="button" className={t3} onClick={onBack}>{t4}{t5}</button>;
      $[18] = onBack;
      $[19] = t5;
      $[20] = t6;
    } else {
      t6 = $[20];
    }
    let t7;
    if ($[21] !== form.title) {
      t7 = <p className={LABEL_CLASS}>{form.title}</p>;
      $[21] = form.title;
      $[22] = t7;
    } else {
      t7 = $[22];
    }
    let t8;
    if ($[23] !== hof.hofName) {
      t8 = <h3 className="font-heading text-heading text-2xl font-extrabold text-balance @xl:text-3xl">{hof.hofName}</h3>;
      $[23] = hof.hofName;
      $[24] = t8;
    } else {
      t8 = $[24];
    }
    let t9;
    if ($[25] !== current || $[26] !== form.deadline || $[27] !== hof.versions || $[28] !== locale || $[29] !== onOpenInDashboard) {
      t9 = current !== undefined && <p className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-green-700"><StatusPill tone={current.status === "revisionRequired" ? "attention" : "neutral"}>{HOF_ENTRY_STATUS_LABELS[current.status][locale]}</StatusPill><span>{translate("versionOn", locale, {
            n: hof.versions.length,
            date: formatDate(current.submittedAt, locale)
          })}{form.deadline !== undefined && ` · ${translate("dueOn", locale, {
            date: formatDate(form.deadline, locale)
          })}`}</span><button type="button" className="text-conveniat-green min-h-11 cursor-pointer font-semibold underline underline-offset-2" onClick={onOpenInDashboard}>{translate("openInDashboard", locale)}</button></p>;
      $[25] = current;
      $[26] = form.deadline;
      $[27] = hof.versions;
      $[28] = locale;
      $[29] = onOpenInDashboard;
      $[30] = t9;
    } else {
      t9 = $[30];
    }
    let t10;
    if ($[31] !== t6 || $[32] !== t7 || $[33] !== t8 || $[34] !== t9) {
      t10 = <div className="space-y-2">{t6}{t7}{t8}{t9}</div>;
      $[31] = t6;
      $[32] = t7;
      $[33] = t8;
      $[34] = t9;
      $[35] = t10;
    } else {
      t10 = $[35];
    }
    t2 = <div className="space-y-6">{t10}{current === undefined ? <Panel><p className="text-base text-green-700">{translate("noOrderYet", locale, {
            hof: hof.hofName
          })}</p></Panel> : <><ReviewActions key={current.id} hofId={hof.hofId} version={current} locale={locale} /><div className="grid items-start gap-6 @4xl:grid-cols-[minmax(0,1fr)_minmax(16rem,22rem)]"><Panel className="overflow-hidden p-0 @xl:p-0"><table className="w-full text-base"><thead className="bg-page"><tr className={LABEL_CLASS}><th scope="col" className="px-5 py-3 text-left font-bold @xl:px-6">{translate("material", locale)}</th><th scope="col" className="px-3 py-3 text-right font-bold">{translate("hofOrdered", locale)}</th><th scope="col" className="px-5 py-3 text-left font-bold @xl:px-6">{translate("change", locale)}</th></tr></thead>{sections.map((t11, index) => {
                const {
                  section,
                  lines: ofSection
                } = t11;
                return <tbody key={section ?? `section-${index}`} className="divide-line divide-y">{section !== undefined && <tr><th scope="colgroup" colSpan={3} className={cn(LABEL_CLASS, "border-line border-t px-5 pt-5 pb-2 text-left @xl:px-6")}>{section}</th></tr>}{ofSection.map(line_0 => {
                    const change = changeLabel(line_0, locale);
                    return <tr key={line_0.item.id}><th scope="row" className="px-5 py-3 text-left font-normal text-green-900 @xl:px-6">{line_0.item.name}</th><td className={cn("px-3 py-3 text-right font-semibold tabular-nums", line_0.quantity === 0 ? "text-gray-400 line-through" : "text-green-900")}>{formatNumber(line_0.quantity, locale)}</td><td className="px-5 py-3 text-sm text-amber-800 tabular-nums @xl:px-6">{change}</td></tr>;
                  })}</tbody>;
              })}</table></Panel><div className="space-y-6">{current.remarks.length > 0 && <Panel className="space-y-3"><SectionHeading className="text-xl">{translate("remarksOfHof", locale)}</SectionHeading><dl className="space-y-3">{current.remarks.map(_temp2)}</dl></Panel>}<Panel className="space-y-4"><div className="flex items-baseline justify-between gap-2"><SectionHeading className="text-xl">{translate("history", locale)}</SectionHeading><span className="text-sm text-green-700">{translate("entriesCount", locale, {
                    n: history.length
                  })}</span></div><Timeline steps={history} locale={locale} /></Panel></div></div></>}</div>;
    $[4] = current;
    $[5] = form;
    $[6] = hof.hofId;
    $[7] = hof.hofName;
    $[8] = hof.versions;
    $[9] = locale;
    $[10] = onBack;
    $[11] = onOpenInDashboard;
    $[12] = sections;
    $[13] = t2;
  } else {
    t2 = $[13];
  }
  return t2;
};
function _temp(open) {
  return !open;
}
function _temp2(remark) {
  return <div key={remark.label}><dt className="text-sm text-green-700">{remark.label}</dt><dd className="text-base whitespace-pre-line text-green-900">{remark.text}</dd></div>;
}